import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Table from '@cloudscape-design/components/table';
import Header from '@cloudscape-design/components/header';
import Alert from '@cloudscape-design/components/alert';
import Button from '@cloudscape-design/components/button';
import Badge from '@cloudscape-design/components/badge';
import Checkbox from '@cloudscape-design/components/checkbox';
import FormField from '@cloudscape-design/components/form-field';
import Select, { SelectProps } from '@cloudscape-design/components/select';
import SpaceBetween from '@cloudscape-design/components/space-between';
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import TextFilter from '@cloudscape-design/components/text-filter';
import Toggle from '@cloudscape-design/components/toggle';
import { Override, getOverrides } from './api';
import BadgePhoto from './BadgePhoto';
import PillarBadge, { PILLAR_DISPLAY } from './Pillar';
import EditModal from './EditModal';
import AddOverrideModal from './AddOverrideModal';

interface Props {
  idToken: string;
}

// Sentinel option value for the "no filter" choice in the Select dropdowns.
const ALL = '__all__';
const ALL_MANAGERS: SelectProps.Option = { label: 'All managers', value: ALL };
const ALL_PILLARS: SelectProps.Option = { label: 'All pillars', value: ALL };

export default function OverridesTable({ idToken }: Props) {
  const [items, setItems] = useState<Override[]>([]);
  const [currentUser, setCurrentUser] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState<Override | null>(null);
  const [adding, setAdding] = useState(false);
  const [showCustomOnly, setShowCustomOnly] = useState(false);
  const [aliasFilter, setAliasFilter] = useState('');
  const [managerFilter, setManagerFilter] = useState<string>(ALL);
  const [pillarFilter, setPillarFilter] = useState<string>(ALL);
  const [onlyMyReports, setOnlyMyReports] = useState(false);
  // Whether we have already applied the one-time default for "my direct
  // reports" (checked by default for managers), so a later refresh does not
  // clobber the user's own toggle.
  const defaultedMyReports = useRef(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await getOverrides(idToken);
      setItems(res.overrides);
      setCurrentUser(res.currentUser.alias);
    } catch (e) {
      // Distinguish a failed fetch from a genuinely empty list.
      setItems([]);
      setError(e instanceof Error ? e.message : 'Failed to load overrides.');
    } finally {
      setLoading(false);
    }
  }, [idToken]);

  useEffect(() => {
    void load();
  }, [load]);

  // The signed-in user is a "manager with direct reports" iff they appear as
  // the direct manager of at least one tester in the pool.
  const isManager = useMemo(
    () => !!currentUser && items.some((i) => i.directManager === currentUser),
    [items, currentUser],
  );

  // Default the "only my direct reports" checkbox to checked for managers,
  // once, after the first load resolves who the user is and their reports.
  useEffect(() => {
    if (loading || defaultedMyReports.current || !currentUser) return;
    setOnlyMyReports(isManager);
    defaultedMyReports.current = true;
  }, [loading, currentUser, isManager]);

  // Distinct direct managers present in the data, for the manager dropdown.
  const managerOptions = useMemo<SelectProps.Option[]>(() => {
    const managers = Array.from(new Set(items.map((i) => i.directManager).filter(Boolean))).sort();
    return [ALL_MANAGERS, ...managers.map((m) => ({ label: m, value: m }))];
  }, [items]);

  // Pillar dropdown: the known pillars, plus any raw value present in the data
  // that we do not have a friendly label for.
  const pillarOptions = useMemo<SelectProps.Option[]>(() => {
    const present = new Set(items.map((i) => i.pillar).filter(Boolean));
    const known = Object.entries(PILLAR_DISPLAY);
    const extras = Array.from(present).filter((p) => !(p in PILLAR_DISPLAY));
    return [
      ALL_PILLARS,
      ...known.map(([value, { label }]) => ({ label, value })),
      ...extras.map((p) => ({ label: p, value: p })),
    ];
  }, [items]);

  const visibleItems = useMemo(() => {
    let filtered = items;
    if (showCustomOnly) {
      filtered = filtered.filter((i) => !i.includeInScheduling && i.priorityScore !== 50);
    }
    if (aliasFilter) {
      const lower = aliasFilter.toLowerCase();
      filtered = filtered.filter((i) => i.testerAlias.toLowerCase().includes(lower));
    }
    // "Only my direct reports" takes precedence over the manager dropdown
    // (which is disabled while it is checked).
    if (onlyMyReports && currentUser) {
      filtered = filtered.filter((i) => i.directManager === currentUser);
    } else if (managerFilter !== ALL) {
      filtered = filtered.filter((i) => i.directManager === managerFilter);
    }
    if (pillarFilter !== ALL) {
      filtered = filtered.filter((i) => i.pillar === pillarFilter);
    }
    return filtered;
  }, [items, showCustomOnly, aliasFilter, onlyMyReports, currentUser, managerFilter, pillarFilter]);

  return (
    <>
      {error && (
        <Alert type="error" header="Failed to load overrides" dismissible onDismiss={() => setError('')}>
          {error}
        </Alert>
      )}
      <Table
        loading={loading}
        loadingText="Loading overrides"
        items={visibleItems}
        filter={
          <SpaceBetween direction="horizontal" size="m">
            <TextFilter
              filteringPlaceholder="Filter by tester alias"
              filteringText={aliasFilter}
              onChange={({ detail }) => setAliasFilter(detail.filteringText)}
            />
            <FormField label="Direct manager">
              <Select
                selectedOption={managerOptions.find((o) => o.value === managerFilter) ?? ALL_MANAGERS}
                options={managerOptions}
                disabled={onlyMyReports}
                onChange={({ detail }) => setManagerFilter(detail.selectedOption.value ?? ALL)}
              />
            </FormField>
            <FormField label="Pillar">
              <Select
                selectedOption={pillarOptions.find((o) => o.value === pillarFilter) ?? ALL_PILLARS}
                options={pillarOptions}
                onChange={({ detail }) => setPillarFilter(detail.selectedOption.value ?? ALL)}
              />
            </FormField>
            <FormField label=" ">
              <Checkbox
                checked={onlyMyReports}
                disabled={!isManager}
                onChange={({ detail }) => setOnlyMyReports(detail.checked)}
              >
                Only my direct reports
              </Checkbox>
            </FormField>
          </SpaceBetween>
        }
        header={
          <Header
            counter={`(${visibleItems.length})`}
            actions={
              <SpaceBetween direction="horizontal" size="m">
                <Toggle checked={showCustomOnly} onChange={({ detail }) => setShowCustomOnly(detail.checked)}>
                  Show available testers only
                </Toggle>
                <Button variant="primary" iconName="add-plus" onClick={() => setAdding(true)}>
                  Add override
                </Button>
                <Button iconName="refresh" onClick={() => void load()} />
              </SpaceBetween>
            }
          >
            Overrides
          </Header>
        }
        empty="No overrides found"
        columnDefinitions={[
          {
            id: 'alias',
            header: 'Tester Alias',
            cell: (item) => <strong>{item.testerAlias}</strong>,
            sortingField: 'testerAlias',
          },
          {
            id: 'directManager',
            header: 'Direct Manager',
            sortingField: 'directManager',
            cell: (item) =>
              item.directManager ? (
                <span className="manager-cell">
                  <BadgePhoto alias={item.directManager} size={28} />
                  {item.directManager}
                </span>
              ) : (
                '—'
              ),
          },
          {
            id: 'pillar',
            header: 'Pillar',
            sortingField: 'pillar',
            cell: (item) => <PillarBadge pillar={item.pillar} />,
          },
          {
            id: 'addedTags',
            header: 'Added Tags',
            cell: (item) => (
              <SpaceBetween direction="horizontal" size="xxs">
                {item.addedTags.map((t) => (
                  <Badge key={t} color="green">
                    {t}
                  </Badge>
                ))}
              </SpaceBetween>
            ),
          },
          {
            id: 'removedTags',
            header: 'Removed Tags',
            cell: (item) => (
              <SpaceBetween direction="horizontal" size="xxs">
                {item.removedTags.map((t) => (
                  <Badge key={t} color="red">
                    {t}
                  </Badge>
                ))}
              </SpaceBetween>
            ),
          },
          {
            id: 'includeInScheduling',
            header: 'In Scheduling',
            cell: (item) =>
              item.includeInScheduling ? (
                <StatusIndicator type="success">Yes</StatusIndicator>
              ) : (
                <StatusIndicator type="error">No</StatusIndicator>
              ),
          },
          {
            id: 'priorityScore',
            header: 'Priority Score',
            cell: (item) => item.priorityScore,
            sortingField: 'priorityScore',
          },
          {
            id: 'comment',
            header: 'Comment',
            cell: (item) => item.comment || '—',
          },
          {
            id: 'lastModified',
            header: 'Last Modified',
            sortingField: 'lastModifiedAt',
            cell: (item) =>
              item.lastModifiedAt ? (
                <span title={new Date(item.lastModifiedAt).toLocaleString()}>
                  {new Date(item.lastModifiedAt).toLocaleDateString()}
                  {item.lastModifiedBy ? ` · ${item.lastModifiedBy}` : ''}
                </span>
              ) : (
                '—'
              ),
          },
          {
            id: 'actions',
            header: 'Actions',
            cell: (item) => (
              <Button variant="inline-link" onClick={() => setEditing(item)}>
                Edit
              </Button>
            ),
          },
        ]}
      />
      {editing && (
        <EditModal
          override={editing}
          idToken={idToken}
          onDismiss={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void load();
          }}
        />
      )}
      {adding && (
        <AddOverrideModal
          idToken={idToken}
          onDismiss={() => setAdding(false)}
          onCreated={() => {
            setAdding(false);
            void load();
          }}
        />
      )}
    </>
  );
}
